"use client";

import { useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useEnumeration } from "@/api/enums/enumsApi";
import { useGetUserQuery } from "@/api/auth/authPrivateApi";
import { getBrandKeyFromEnv } from "@/lib/branding";
import { DANGER_ACTION_KEYS, LOCAL_DANGER_ACTIONS } from "./dangerActions";
import { getSchoolVerdictPreset, presetActionsFor } from "./schoolVerdictPresets";
import type { VerdictStatus } from "./types";

export interface DangerAction {
  raw: string;
  name: string;
}

/**
 * Каталог danger-действий (raw + локализованное name) и резолвер подписи
 * по ключу. Источник — бэкенд-перечисление AssignmentActionType, поэтому
 * подписи приходят на языке смотрящего и совпадают с AI-отчётом.
 *
 * Сверху добавляются наши собственные признаки из LOCAL_DANGER_ACTIONS: их в
 * перечислении нет, подпись берётся из переводов. В статистику AI-отчёта они не
 * попадают — там считаются только настоящие action_type.
 *
 * Если у школы свой перечень (schoolVerdictPresets), общий каталог заменяется
 * им целиком: заказчик просил короткий список, а не дополнение к общему.
 * Подписи при этом резолвятся как обычно, поэтому старые комментарии с любыми
 * признаками продолжают читаться.
 */
export function useDangerActions(status?: VerdictStatus | null) {
  const locale = useLocale();
  const t = useTranslations();
  const { data } = useEnumeration("AssignmentActionType", locale);
  const { data: user } = useGetUserQuery();

  const byRaw = useMemo(
    () => new Map((data ?? []).map((i) => [String(i.raw), i.name] as const)),
    [data],
  );

  const localByRaw = useMemo(
    () => new Map(LOCAL_DANGER_ACTIONS.map((a) => [a.raw, t(a.labelKey)] as const)),
    [t],
  );

  const labelOf = useMemo(
    () => (raw: string) => localByRaw.get(raw) ?? byRaw.get(raw) ?? raw,
    [byRaw, localByRaw],
  );

  const preset = useMemo(
    () =>
      getSchoolVerdictPreset(
        getBrandKeyFromEnv(),
        (user as { school_id?: number | null } | undefined)?.school_id ?? null,
      ),
    [user],
  );

  // Только те danger-ключи, что реально есть в перечислении (иначе сырой raw),
  // а следом — наши собственные, кроме скрытых из пикера.
  const catalog = useMemo<DangerAction[]>(() => {
    const presetKeys = presetActionsFor(preset, status ?? null);
    if (presetKeys) {
      return presetKeys.map((raw) => ({ raw, name: labelOf(raw) }));
    }

    return [
      ...DANGER_ACTION_KEYS.flatMap((raw) => {
        const name = byRaw.get(raw);
        return name ? [{ raw, name }] : [];
      }),
      ...LOCAL_DANGER_ACTIONS.filter((a) => !a.hidden).map((a) => ({
        raw: a.raw,
        name: localByRaw.get(a.raw) ?? a.raw,
      })),
    ];
  }, [byRaw, labelOf, localByRaw, preset, status]);

  /**
   * Что подставляется само при статусе «Честно» у школ со своим перечнем.
   * Не зависит от текущего status: значение нужно в момент переключения тогла,
   * когда status ещё старый.
   */
  const honestAutofill = useMemo<string[]>(() => preset?.honest ?? [], [preset]);

  /**
   * У школьного перечня пикер показывается простым списком: делить два-три
   * пункта на «недавние» и «все» незачем.
   */
  const plainList = Boolean(preset);

  return { catalog, labelOf, honestAutofill, plainList };
}
